/**
 * "What you do here" rail (P107) -- a light, dismissible top-banner on
 * a view that names the audience role + the entry-point question + a
 * one-link CTA into the most relevant place. Renders null once dismissed.
 *
 * P115.2: dismissals are now server-side (cross-device) via the
 * user-preferences store, not localStorage. Renders null while
 * preferences load to avoid a flash-then-hide.
 *
 * Copy lives in i18n under `viewRail.<key>.{audience, question, cta}`;
 * the CTA route lives in VIEW_RAIL so there's one source of truth for
 * "what is this view + who's it for".
 */
import { useMe } from "shared/api/useMe";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useDismissRail, useUserPreferences } from "shared/api/userPreferences";

/** Known view rails -> the CTA route. Copy is in i18n by the same key. */
export const VIEW_RAIL: Record<string, { ctaTo: string; adminOnly?: boolean }> = {
  // H7: this rail is addressed to "data stewards + CMDB owners" and
  // its call to action is an admin-only register. A reader who is
  // neither was being given a job they cannot do and a link that
  // refuses them. A prompt aimed at the wrong person is worse than no
  // prompt: it reads as the product not knowing who you are.
  foundation: { ctaTo: "/foundation/admin/ibses", adminOnly: true },
  twin: { ctaTo: "/twin/cascade" },
  framework: { ctaTo: "/framework/assessments" },
  // P266: the "what next" rail on the assessment cycle grid -- after
  // grading, the Recommendations inbox is where every gap gets actioned.
  framework_assessments: { ctaTo: "/recommendations" },
};

export function ViewRail({ viewKey }: { viewKey: string }) {
  const { t } = useTranslation();
  const config = VIEW_RAIL[viewKey];
  const me = useMe();
  const prefs = useUserPreferences();
  const dismiss = useDismissRail();
  const [optimisticHidden, setOptimisticHidden] = useState(false);

  const current = prefs.data?.dismissed_rails ?? [];
  const dismissed = optimisticHidden || current.includes(viewKey);

  // Avoid a flash-then-hide: wait until preferences resolve.
  if (!config || prefs.isLoading || dismissed) return null;
  // And wait for the profile before deciding on an admin-only rail,
  // so it does not flash for a reader who should never see it.
  if (config.adminOnly && me.data?.is_admin !== true) return null;

  const onDismiss = () => {
    setOptimisticHidden(true);
    dismiss.mutate({ rail: viewKey, current });
  };

  return (
    <aside
      data-testid="view-rail"
      data-view={viewKey}
      className="flex items-start gap-3 rounded-lg border border-brand-brass/30 bg-brand-brass/5 px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-brassDark">
          {t(`viewRail.${viewKey}.audience`)}
        </p>
        <p className="mt-0.5 text-sm text-slate-700">
          {t(`viewRail.${viewKey}.question`)}
        </p>
        <Link
          data-testid="view-rail-cta"
          to={config.ctaTo}
          className="mt-1 inline-block text-sm font-semibold text-brand-brassDark hover:underline"
        >
          {t(`viewRail.${viewKey}.cta`)} {t("screens.viewRail.rarr")}
        </Link>
      </div>
      <button
        type="button"
        data-testid="view-rail-dismiss"
        onClick={onDismiss}
        aria-label={t("viewRail.dismiss")}
        className="flex-shrink-0 rounded px-1.5 text-slate-400 hover:text-slate-700"
      >
        ✕
      </button>
    </aside>
  );
}
